// PDF & WhatsApp Share helpers
import html2pdf from "html2pdf.js";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PDF_BASE_STYLE = `
  *{box-sizing:border-box}
  html,body{margin:0;padding:0;background:#fff;color:#000}
  body,div,p,td,th,h1,h2,h3,h4,table{font-family:'Tajawal','Cairo','Segoe UI',Tahoma,Arial,sans-serif !important;color:#000}
  table{border-collapse:collapse;width:100%}
`;

const waitForFonts = async () => {
  try {
    if (document.fonts && document.fonts.ready) await document.fonts.ready;
  } catch {/**/}
};

// Render HTML inside an iframe (isolated document) then run html2pdf on its body.
// iframe stays in DOM with width=A4 px and visible position 0,0 but is briefly displayed
// behind any overlays; we remove it as soon as the PDF is saved.
const renderInIframe = async (html, filename, opts = {}) => {
  await waitForFonts();
  const widthPx = opts.format === "a5" ? 559 : 794;

  const iframe = document.createElement("iframe");
  iframe.style.position = "fixed";
  iframe.style.top = "0";
  iframe.style.left = "0";
  iframe.style.width = `${widthPx}px`;
  iframe.style.height = "100vh";
  iframe.style.border = "0";
  iframe.style.zIndex = "-1";
  iframe.style.opacity = "0";
  iframe.style.pointerEvents = "none";
  document.body.appendChild(iframe);

  try {
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html dir="rtl" lang="ar"><head><meta charset="UTF-8">
      <link rel="preconnect" href="https://fonts.googleapis.com">
      <link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;900&display=swap" rel="stylesheet">
      <style>${PDF_BASE_STYLE}</style></head>
      <body>${html}</body></html>`);
    doc.close();

    // wait for the iframe document to be ready + fonts to load
    await new Promise((r) => {
      if (doc.readyState === "complete") r();
      else iframe.addEventListener("load", r, { once: true });
    });
    try {
      if (doc.fonts && doc.fonts.ready) await doc.fonts.ready;
    } catch {/**/}
    await new Promise((r) => setTimeout(r, 400));

    const target = doc.body;
    await html2pdf()
      .from(target)
      .set({
        margin: opts.margin ?? [5, 5, 5, 5],
        filename: filename || "document.pdf",
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          letterRendering: true,
          backgroundColor: "#ffffff",
          logging: false,
          width: widthPx,
          windowWidth: widthPx,
        },
        jsPDF: { unit: "mm", format: opts.format || "a4", orientation: opts.orientation || "portrait" },
        pagebreak: { mode: ["css", "legacy"] },
      })
      .save();
  } finally {
    iframe.remove();
  }
};

/**
 * Generate a PDF from raw HTML string.
 */
export const generatePDFFromHTML = async (html, filename = "document.pdf", opts = {}) => {
  await renderInIframe(html, filename, opts);
};

/**
 * Generate a PDF directly from a live DOM element. Serializes its outerHTML into the iframe.
 */
export const generatePDFFromElement = async (element, filename = "document.pdf", opts = {}) => {
  if (!element) throw new Error("element required");
  // Copy all <link rel="stylesheet"> and <style> tags so Tailwind/global CSS applies in the iframe.
  const styleNodes = Array.from(
    document.querySelectorAll('link[rel="stylesheet"], style')
  ).map((el) => el.outerHTML).join("\n");
  const html = `${styleNodes}\n${element.outerHTML}`;
  await renderInIframe(html, filename, opts);
};

/**
 * Get a Blob from raw HTML (for Web Share). Renders in iframe and outputs blob.
 */
export const generatePDFBlob = async (html, opts = {}) => {
  await waitForFonts();
  const widthPx = opts.format === "a5" ? 559 : 794;
  const iframe = document.createElement("iframe");
  iframe.style.cssText = `position:fixed;top:0;left:0;width:${widthPx}px;height:100vh;border:0;z-index:-1;opacity:0;pointer-events:none`;
  document.body.appendChild(iframe);
  try {
    const doc = iframe.contentDocument || iframe.contentWindow.document;
    doc.open();
    doc.write(`<!DOCTYPE html><html dir="rtl"><head><meta charset="UTF-8">
      <link href="https://fonts.googleapis.com/css2?family=Tajawal:wght@400;500;700;900&display=swap" rel="stylesheet">
      <style>${PDF_BASE_STYLE}</style></head><body>${html}</body></html>`);
    doc.close();
    await new Promise((r) => {
      if (doc.readyState === "complete") r();
      else iframe.addEventListener("load", r, { once: true });
    });
    try { if (doc.fonts && doc.fonts.ready) await doc.fonts.ready; } catch {/**/}
    await new Promise((r) => setTimeout(r, 400));
    const blob = await html2pdf()
      .from(doc.body)
      .set({
        margin: opts.margin ?? [5, 5, 5, 5],
        image: { type: "jpeg", quality: 0.98 },
        html2canvas: { scale: 2, useCORS: true, backgroundColor: "#ffffff", logging: false, width: widthPx, windowWidth: widthPx },
        jsPDF: { unit: "mm", format: opts.format || "a4", orientation: opts.orientation || "portrait" },
      })
      .outputPdf("blob");
    return blob;
  } finally {
    iframe.remove();
  }
};

/**
 * Format an Iraqi phone number for wa.me (needs country code 964).
 * Accepts 07XXXXXXXXX, +964XXXXXXXXXX, etc.
 */
export const normalizeWhatsAppNumber = (phone) => {
  if (!phone) return "";
  let p = String(phone).replace(/[^0-9]/g, "");
  if (p.startsWith("00964")) p = p.slice(5);
  else if (p.startsWith("964")) p = p.slice(3);
  else if (p.startsWith("0")) p = p.slice(1);
  return `964${p}`;
};

/**
 * Open WhatsApp chat (mobile app or web) with prefilled text. If phone supplied,
 * the chat opens directly with that contact; otherwise the user picks.
 */
export const shareWhatsApp = (text, phone) => {
  const encoded = encodeURIComponent(text);
  const num = normalizeWhatsAppNumber(phone);
  const url = num ? `https://wa.me/${num}?text=${encoded}` : `https://wa.me/?text=${encoded}`;
  window.open(url, "_blank", "noopener,noreferrer");
};

/**
 * Try to share a generated PDF using the Web Share API (mobile WhatsApp pickable).
 * Falls back to: download PDF + open wa.me with text.
 */
export const shareWhatsAppWithPDF = async (html, filename, text, phone, opts = {}) => {
  try {
    const blob = await generatePDFBlob(html, opts);
    const file = new File([blob], filename, { type: "application/pdf" });
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text, title: filename });
      return;
    }
  } catch (e) {
    // ignore; we'll fall back
  }
  // Fallback: download + open WA
  try {
    const blob = await generatePDFBlob(html, opts);
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  } catch {/**/}
  shareWhatsApp(text, phone);
};

/**
 * Download FULL system backup (all collections, all data) as a single JSON file.
 * The same file can be re-uploaded via /api/admin/import/full-backup to restore.
 */
export const downloadDailyBackup = async () => {
  const token = localStorage.getItem("jumlaty_token");
  const res = await fetch(`${API}/admin/export/full-backup`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Backup failed");
  const blob = await res.blob();
  // Use Content-Disposition filename if available, fallback to default
  let fname = `jumlaty_full_backup_${new Date().toISOString().slice(0, 10)}.json`;
  const cd = res.headers.get("content-disposition") || "";
  const m = cd.match(/filename="([^"]+)"/);
  if (m) fname = m[1];
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = fname;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(link.href), 1000);
  return res.headers.get("x-total-documents") || "?";
};

/**
 * Upload a full-backup JSON file to restore the system.
 * mode: "merge" = add/update by id (safe) | "replace" = wipe then insert (destructive).
 */
export const restoreFullBackup = async (file, mode = "merge") => {
  const token = localStorage.getItem("jumlaty_token");
  const form = new FormData();
  form.append("file", file);
  const res = await fetch(`${API}/admin/import/full-backup?mode=${encodeURIComponent(mode)}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
    body: form,
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data?.detail || "Restore failed");
  return data;
};
